/**
 * Shared cart Section Rendering API helpers — used by <cart-rewards>
 * (assets/custom-cart-rewards.js) and <cart-line-toggle>
 * (assets/custom-cart-line-toggle.js). Classic script, loaded from
 * theme.liquid's stable DOM before either consumer.
 *
 * The section list mirrors the drawer/page/icon-bubble entries of Dawn's own
 * getSectionsToRender() implementations — kept in sync by hand.
 */

/**
 * The cart surfaces present on this page, as Section Rendering API requests.
 * Call BEFORE mutating the cart: dataset.id lookups need the elements to
 * still exist (same reason CartItems.updateQuantity caches its list).
 */
function cartSectionsToRender() {
  const sections = [];
  if (document.getElementById('CartDrawer')) {
    sections.push({ section: 'cart-drawer', targetId: 'CartDrawer', selector: '.drawer__inner' });
  }
  const mainCartItems = document.getElementById('main-cart-items');
  if (mainCartItems?.dataset.id) {
    sections.push({ section: mainCartItems.dataset.id, targetId: 'main-cart-items', selector: '.js-contents' });
  }
  const mainCartFooter = document.getElementById('main-cart-footer');
  if (mainCartFooter?.dataset.id && mainCartFooter.dataset.id !== mainCartItems?.dataset.id) {
    sections.push({
      section: mainCartFooter.dataset.id,
      targetId: 'main-cart-footer',
      selector: '.js-contents',
    });
  }
  if (document.getElementById('cart-icon-bubble')) {
    sections.push({ section: 'cart-icon-bubble', targetId: 'cart-icon-bubble', selector: '.shopify-section' });
  }
  return sections;
}

/* -------------------------------------------------------------------------
   In-flight cart state
   -------------------------------------------------------------------------

   ONE flag — `cart-busy` on <html> — says "a cart mutation is in flight, the
   numbers on screen are stale". The stale treatment itself is entirely in CSS
   (custom-cart.css); this owns only the flag.

   It has to live on <html> because the values that go stale have no common
   ancestor below <body>: the line's price sits in the items list, the totals
   in #main-cart-footer, the rewards rail outside <cart-drawer-items> in the
   drawer. Per-surface flags would mean three of them and three chances for one
   to be left set.

   Two ways it gets set:
     - our own mutation paths call setCartBusy() directly (<cart-line-toggle>,
       <cart-rewards>),
     - Dawn's own quantity/remove path cannot — cart.js is stock and stays
       untouched — so the observer below mirrors the `cart__items--disabled`
       class it toggles on the items container.

   Counted, not boolean: the rewards reconciler routinely mutates the cart
   while a quantity change is still settling, and whichever finished first
   would otherwise clear the flag out from under the other.
*/

let cartBusyCount = 0;

/** Is Dawn's own in-flight class currently on either items container? */
function cartBusyDawn() {
  return !!document.querySelector('.cart__items--disabled');
}

function syncCartBusy() {
  document.documentElement.classList.toggle('cart-busy', cartBusyCount > 0 || cartBusyDawn());
}

function setCartBusy(busy) {
  cartBusyCount = Math.max(0, cartBusyCount + (busy ? 1 : -1));
  syncCartBusy();
}

/**
 * Mirror Dawn's flag. Attribute-filtered to `class` and resolved by a single
 * querySelector, so the callback stays cheap on a page whose class attributes
 * barely churn. Document-level with a dynamic lookup for the same reason the
 * rewards observer is: the items containers are themselves replaced wholesale
 * by section swaps, which would strand an observer bound to them.
 */
if (typeof MutationObserver === 'function') {
  new MutationObserver(syncCartBusy).observe(document.documentElement, {
    attributes: true,
    subtree: true,
    attributeFilter: ['class'],
  });
}

/** Swap freshly rendered section HTML into the live DOM, Dawn-style. */
function renderCartSections(sectionsHtml, sectionsToRender) {
  for (const entry of sectionsToRender) {
    const html = sectionsHtml[entry.section];
    if (!html) continue;
    const targetRoot = document.getElementById(entry.targetId);
    if (!targetRoot) continue;
    const target = targetRoot.querySelector(entry.selector) ?? targetRoot;
    const source = new DOMParser().parseFromString(html, 'text/html').querySelector(entry.selector);
    if (source) target.innerHTML = source.innerHTML;
  }
}
